import moment from 'moment'

/**
 * Parse an API date-time string and return a 12-hour formatted time with AM/PM.
 * Accepts formats like '23-Jan-2026 15:00:00:IST' or '2026-01-23 15:00:00' or ISO strings.
 * Returns 'N/A' for invalid inputs.
 */
export const formatTimeFromApi = (dateTimeStr?: string): string => {
  if (!dateTimeStr) return 'N/A'

  try {
    // Remove timezone tokens like :IST and trim
    const cleaned = dateTimeStr.split(':IST')[0].trim()

    // Try common API format first: 'DD-MMM-YYYY HH:mm:ss' then ISO fallback
    const formatsToTry = [
      // eslint-disable-next-line sonarjs/no-duplicate-string
      'DD-MMM-YYYY HH:mm:ss',
      'YYYY-MM-DD HH:mm:ss',
      moment.ISO_8601,
    ]

    for (const fmt of formatsToTry) {
      const m =
        fmt === moment.ISO_8601 ? moment(cleaned) : moment(cleaned, fmt, true)
      if (m.isValid()) {
        return m.format('h:mm A')
      }
    }

    // As a last resort try native Date parsing
    const d = new Date(cleaned)
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    }

    return 'N/A'
  } catch (e) {
    // Fail silently but return placeholder
    // eslint-disable-next-line no-console
    console.warn('formatTimeFromApi failed to parse', dateTimeStr, e)
    return 'N/A'
  }
}

export const localeDateFormat = (date: string): string => {
  if (date) {
    const deviceLocale: string =
      navigator.languages && navigator.languages.length
        ? navigator.languages[0]
        : navigator.language

    const tempDate = date
    const dateParts: string[] | string = date ? tempDate.split('/') : ''
    const newDate = date
      ? new Date(
          Number(dateParts[2]),
          Number(dateParts[1]) - 1,
          Number(dateParts[0]),
        )
      : new Date()

    return newDate.toLocaleDateString(deviceLocale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  } else {
    return ''
  }
}

export const reformatDate = (date: string): Date => {
  const dateParts: string[] | string = date ? date.split('/') : ''
  return date
    ? new Date(
        Number(dateParts[2]),
        Number(dateParts[1]) - 1,
        Number(dateParts[0]),
      )
    : new Date()
}

const format1 = 'yyyy/mm/dd'
const format2 = 'dd/mm/yyyy'
const format3 = 'dd/mm/yyyy'
const format4 = 'dd.mm.yyyy'
const format5 = 'd. m. yyyy'
const format6 = 'mm/dd/yyyy'
const format7 = 'yyyy-mm-dd'
const format8 = 'mm/dd/yyyy'
export const dateFormatPerLocale = [
  { label: 'af-ZA', format: format1 },
  { label: 'am-ET', format: 'd/M/yyyy' },
  { label: 'ar-AE', format: format2 },
  { label: 'ar-BH', format: format2 },
  { label: 'ar-DZ', format: format3 },
  { label: 'ar-EG', format: format2 },
  { label: 'ar-IQ', format: format2 },
  { label: 'ar-JO', format: format2 },
  { label: 'ar-KW', format: format2 },
  { label: 'ar-LB', format: format2 },
  { label: 'ar-LY', format: format2 },
  { label: 'ar-MA', format: format3 },
  { label: 'ar-OM', format: format2 },
  { label: 'ar-QA', format: format2 },
  { label: 'ar-SA', format: 'dd/MM/yy' },
  { label: 'ar-SY', format: format2 },
  { label: 'ar-TN', format: format3 },
  { label: 'ar-YE', format: format2 },
  { label: 'arn-CL', format: format3 },
  { label: 'as-IN', format: format3 },
  { label: 'az-Cyrl-AZ', format: format4 },
  { label: 'az-Latn-AZ', format: format4 },
  { label: 'ba-RU', format: 'dd.MM.yy' },
  { label: 'be-BY', format: format4 },
  { label: 'bg-BG', format: 'dd.M.yyyy' },
  { label: 'bn-BD', format: 'dd-MM-yy' },
  { label: 'bn-IN', format: 'dd-MM-yy' },
  { label: 'bo-CN', format: 'yyyy/M/d' },
  { label: 'br-FR', format: format2 },
  { label: 'bs-Cyrl-BA', format: 'd.M.yyyy' },
  { label: 'bs-Latn-BA', format: 'd.M.yyyy' },
  { label: 'ca-ES', format: format2 },
  { label: 'co-FR', format: format2 },
  { label: 'cs-CZ', format: 'd.M.yyyy' },
  { label: 'cy-GB', format: format2 },
  { label: 'da-DK', format: format3 },
  { label: 'de-AT', format: format4 },
  { label: 'de-CH', format: format4 },
  { label: 'de-DE', format: format4 },
  { label: 'de-LI', format: format4 },
  { label: 'de-LU', format: format4 },
  { label: 'dsb-DE', format: format5 },
  { label: 'dv-MV', format: 'dd/MM/yy' },
  { label: 'el-GR', format: 'd/M/yyyy' },
  { label: 'en', format: format2 },
  { label: 'en-029', format: format6 },
  { label: 'en-AU', format: 'dd/MM/yyyy' },
  { label: 'en-BZ', format: format2 },
  { label: 'en-CA', format: format2 },
  { label: 'en-GB', format: format2 },
  { label: 'en-IE', format: format2 },
  { label: 'en-IN', format: 'dd/MM/yyyy' },
  { label: 'en-JM', format: format2 },
  { label: 'en-MY', format: 'd/M/yyyy' },
  { label: 'en-NZ', format: 'd/MM/yyyy' },
  { label: 'en-PH', format: format8 },
  { label: 'en-SG', format: 'd/M/yyyy' },
  { label: 'en-TT', format: format2 },
  { label: 'en-US', format: format8 },
  { label: 'en-ZA', format: format1 },
  { label: 'en-ZW', format: format8 },
  { label: 'es-AR', format: format2 },
  { label: 'es-BO', format: format2 },
  { label: 'es-CL', format: format3 },
  { label: 'es-CO', format: format2 },
  { label: 'es-CR', format: format2 },
  { label: 'es-DO', format: format2 },
  { label: 'es-EC', format: format2 },
  { label: 'es-ES', format: format2 },
  { label: 'es-GT', format: format2 },
  { label: 'es-HN', format: format2 },
  { label: 'es-MX', format: format2 },
  { label: 'es-NI', format: format2 },
  { label: 'es-PA', format: format6 },
  { label: 'es-PE', format: format2 },
  { label: 'es-PR', format: format2 },
  { label: 'es-PY', format: format2 },
  { label: 'es-SV', format: format2 },
  { label: 'es-US', format: format8 },
  { label: 'es-UY', format: format2 },
  { label: 'es-VE', format: format2 },
  { label: 'et-EE', format: 'd.MM.yyyy' },
  { label: 'eu-ES', format: format1 },
  { label: 'fa-IR', format: format6 },
  { label: 'fi-FI', format: 'd.M.yyyy' },
  { label: 'fil-PH', format: format8 },
  { label: 'fo-FO', format: format3 },
  { label: 'fr-BE', format: 'd/MM/yyyy' },
  { label: 'fr-CA', format: format7 },
  { label: 'fr-CH', format: format4 },
  { label: 'fr-FR', format: format2 },
  { label: 'fr-LU', format: format2 },
  { label: 'fr-MC', format: format2 },
  { label: 'fy-NL', format: 'd-M-yyyy' },
  { label: 'ga-IE', format: format2 },
  { label: 'gd-GB', format: format2 },
  { label: 'gl-ES', format: 'dd/MM/yy' },
  { label: 'gsw-FR', format: format2 },
  { label: 'gu-IN', format: 'dd-MM-yy' },
  { label: 'ha-Latn-NG', format: 'd/M/yyyy' },
  { label: 'he-IL', format: format2 },
  { label: 'hi-IN', format: format3 },
  { label: 'hr-BA', format: 'd.M.yyyy.' },
  { label: 'hr-HR', format: 'd.M.yyyy' },
  { label: 'hsb-DE', format: format5 },
  { label: 'hu-HU', format: 'yyyy. MM. dd.' },
  { label: 'hy-AM', format: format4 },
  { label: 'id-ID', format: format2 },
  { label: 'ig-NG', format: 'd/M/yyyy' },
  { label: 'ii-CN', format: 'yyyy/M/d' },
  { label: 'is-IS', format: 'd.M.yyyy' },
  { label: 'it-CH', format: format4 },
  { label: 'it-IT', format: format2 },
  { label: 'iu-Cans-CA', format: 'd/M/yyyy' },
  { label: 'iu-Latn-CA', format: 'd/MM/yyyy' },
  { label: 'ja-JP', format: format1 },
  { label: 'ka-GE', format: format4 },
  { label: 'kk-KZ', format: format4 },
  { label: 'kl-GL', format: format3 },
  { label: 'km-KH', format: format7 },
  { label: 'kn-IN', format: 'dd-MM-yy' },
  { label: 'ko-KR', format: 'yyyy. MM. dd' },
  { label: 'kok-IN', format: format3 },
  { label: 'ky-KG', format: 'dd.MM.yy' },
  { label: 'lb-LU', format: format2 },
  { label: 'lo-LA', format: format2 },
  { label: 'lt-LT', format: 'yyyy.MM.dd' },
  { label: 'lv-LV', format: 'yyyy.MM.dd.' },
  { label: 'mi-NZ', format: format2 },
  { label: 'mk-MK', format: format4 },
  { label: 'ml-IN', format: 'dd-MM-yy' },
  { label: 'mn-MN', format: 'yy.MM.dd' },
  { label: 'mn-Mong-CN', format: 'yyyy/M/d' },
  { label: 'moh-CA', format: format8 },
  { label: 'mr-IN', format: format3 },
  { label: 'ms-BN', format: format2 },
  { label: 'ms-MY', format: format2 },
  { label: 'mt-MT', format: format2 },
  { label: 'nb-NO', format: format4 },
  { label: 'ne-NP', format: format8 },
  { label: 'nl-BE', format: 'd/MM/yyyy' },
  { label: 'nl-NL', format: 'd-M-yyyy' },
  { label: 'nn-NO', format: format4 },
  { label: 'nso-ZA', format: format1 },
  { label: 'oc-FR', format: format2 },
  { label: 'or-IN', format: 'dd-MM-yy' },
  { label: 'pa-IN', format: 'dd-MM-yy' },
  { label: 'pl-PL', format: format4 },
  { label: 'prs-AF', format: 'dd/MM/yy' },
  { label: 'ps-AF', format: 'dd/MM/yy' },
  { label: 'pt-BR', format: 'd/M/yyyy' },
  { label: 'pt-PT', format: format3 },
  { label: 'qut-GT', format: format2 },
  { label: 'quz-BO', format: format2 },
  { label: 'quz-EC', format: format2 },
  { label: 'quz-PE', format: format2 },
  { label: 'rm-CH', format: format2 },
  { label: 'ro-RO', format: format4 },
  { label: 'ru-RU', format: format4 },
  { label: 'rw-RW', format: format8 },
  { label: 'sa-IN', format: format3 },
  { label: 'sah-RU', format: 'MM.dd.yyyy' },
  { label: 'se-FI', format: 'd.M.yyyy' },
  { label: 'se-NO', format: format4 },
  { label: 'se-SE', format: format7 },
  { label: 'si-LK', format: format7 },
  { label: 'sk-SK', format: format5 },
  { label: 'sl-SI', format: 'd.M.yyyy' },
  { label: 'sma-NO', format: format4 },
  { label: 'sma-SE', format: format7 },
  { label: 'smj-NO', format: format4 },
  { label: 'smj-SE', format: format7 },
  { label: 'smn-FI', format: 'd.M.yyyy' },
  { label: 'sms-FI', format: 'd.M.yyyy' },
  { label: 'sq-AL', format: format7 },
  { label: 'sr-Cyrl-BA', format: 'd.M.yyyy' },
  { label: 'sr-Cyrl-CS', format: 'd.M.yyyy' },
  { label: 'sr-Cyrl-ME', format: 'd.M.yyyy' },
  { label: 'sr-Cyrl-RS', format: 'd.M.yyyy' },
  { label: 'sr-Latn-BA', format: 'd.M.yyyy' },
  { label: 'sr-Latn-CS', format: 'd.M.yyyy' },
  { label: 'sr-Latn-ME', format: 'd.M.yyyy' },
  { label: 'sr-Latn-RS', format: 'd.M.yyyy' },
  { label: 'sv-FI', format: 'd.M.yyyy' },
  { label: 'sv-SE', format: format7 },
  { label: 'sw-KE', format: format8 },
  { label: 'syr-SY', format: format2 },
  { label: 'ta-IN', format: format3 },
  { label: 'te-IN', format: 'dd-MM-yy' },
  { label: 'tg-Cyrl-TJ', format: 'dd.MM.yy' },
  { label: 'th-TH', format: 'd/M/yyyy' },
  { label: 'tk-TM', format: 'dd.MM.yy' },
  { label: 'tn-ZA', format: format1 },
  { label: 'tr-TR', format: format4 },
  { label: 'tt-RU', format: format4 },
  { label: 'tzm-Latn-DZ', format: format3 },
  { label: 'ug-CN', format: 'yyyy-M-d' },
  { label: 'uk-UA', format: format4 },
  { label: 'ur-PK', format: format2 },
  { label: 'uz-Cyrl-UZ', format: format4 },
  { label: 'uz-Latn-UZ', format: 'dd/MM yyyy' },
  { label: 'vi-VN', format: format2 },
  { label: 'wo-SN', format: format2 },
  { label: 'xh-ZA', format: format1 },
  { label: 'yo-NG', format: 'd/M/yyyy' },
  { label: 'zh-CN', format: 'yyyy/M/d' },
  { label: 'zh-HK', format: 'd/M/yyyy' },
  { label: 'zh-MO', format: 'd/M/yyyy' },
  { label: 'zh-SG', format: 'd/M/yyyy' },
  { label: 'zh-TW', format: 'yyyy/M/d' },
  { label: 'zu-ZA', format: format1 },
]

export const commonDateFormat = 'L'
const payloadDateFormat = 'DD/MM/YYYY'

export const deviceLocale: string =
  navigator.languages && navigator.languages.length
    ? navigator.languages[0]
    : navigator.language

export const DateHelper = (date: Date | string, format: string): Date => {
  if (!date) {
    return new Date()
  }

  if (String(date) === 'Invalid Date') {
    return new Date()
  }

  if (date instanceof Date) return date
  return moment(date, format).toDate()
}

/**
 * Formats a Date object or date string into a specified format using Moment.js.
 * @param {Date | string} date - The Date object or date string to be formatted.
 * @param {string} [format=payloadDateFormat] - The format string specifying the desired date format.
 *                                              Default is set to payloadDateFormat ('DD/MM/YYYY').
 * @returns {string} The formatted date string.
 */
export const FormatDateHelper = (
  date: Date | string,
  format: string = payloadDateFormat,
): string => {
  if (!date) {
    return ''
  }

  if (String(date) === 'Invalid Date') {
    return ''
  }

  return moment(date).format(format)
}
/**
 * Formats a date string into a specified format using Moment.js.
 * @param {string} date - The date string to be formatted.
 * @param {string} [format=payloadDateFormat] - The format string specifying the desired date format.
 *                                              Default is set to payloadDateFormat ('DD/MM/YYYY').
 * @returns {string} The formatted date string.
 */
export const FormatDateStringHelper = (
  date: string,
  format: string = payloadDateFormat,
): string => {
  if (!date) {
    return ''
  }

  try {
    const dateString = moment(date).format(format)

    console.debug('Date format string helper: ', dateString)
    return dateString
  } catch (e) {
    console.error(e)
    return ''
  }
}

export const dateValidation = (startDate: Date, endDate: Date): boolean => {
  const newStartDate = startDate.setHours(0, 0, 0, 0)
  const newEndtDate = endDate.setHours(0, 0, 0, 0)
  return newStartDate > newEndtDate
}

export const dateValueHelper = (date: string): string => {
  return `${String(new Date(date).getDate()).padStart(2, '0')}/${String(
    new Date(date).getMonth() + 1,
  ).padStart(2, '0')}/${String(new Date(date).getFullYear())}`
}

/**
 * Returns the date range for the previous month.
 *
 * @param {boolean} [untilThisMonth=false] - If true, the end date will be the last day of the current month.
 *                                           If false, the end date will be the last day of the previous month.
 * @returns {{ start: string, end: string }} - An object containing the start and end dates of the previous month
 *                                            formatted as 'DD/MM/YYYY'.
 */
export const getPreviousMonthDateRange = (
  untilThisMonth = false,
): { start: string; end: string } => {
  const now = new Date()
  const startOfMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const endOfMonth = untilThisMonth
    ? new Date(now.getFullYear(), now.getMonth() + 1, 0)
    : new Date(now.getFullYear(), now.getMonth(), 0)
  const formatDate = (date: Date) => {
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }
  return {
    start: formatDate(startOfMonth),
    end: formatDate(endOfMonth),
  }
}

// =========================================================== //
/* ANY CODES TO BE ADDED MUST BE ADDED ABOVE HERE ^^^^ */
/**
 * EVERYTHING STARTS FROM HERE ARE REPLACEMENT FUNCTIONS FOR DATE VALIDATIONS
 * !PLEASE FOLLOW INSTRUCTIONS, DO NOT ADD ANY CODE BELOW THIS LINE
 * FOR THE SAKE OF CODE READABILITY AND MAINTAINABILITY
 */

enum DateBaseEnum {
  Year = 'year',
  Years = 'years',
  Y = 'y',
  Month = 'month',
  Months = 'months',
  M = 'm',
  Day = 'day',
  Days = 'days',
  D = 'd',
}
function isDateBaseEnumValue(value: string): value is DateBaseEnum {
  return Object.values(DateBaseEnum).includes(value as DateBaseEnum)
}

/**
 * Represents a date with day, month, and year properties.
 */
export type ExtractDateType = {
  day: number
  month: number
  year: number
}

/**
 * Represents the config properties of Date Validation Comparators.
 * @property inclusivity - Whether the comparison should be inclusive (default is false).
 * @property granularity - The level of granularity for the comparison (default is 'day').
 */
export type DateComparisonValidationConfigType = {
  inclusivity?: boolean
  granularity?: string
}

/**
 * Extracts the date components (day, month, year) from a given date string based on the provided format.
 *
 * @param date - The date string to be parsed.
 * @param format - The format of the date string (e.g., "DD-MM-YYYY").
 * @returns An object containing the extracted day, month, and year.
 * @throws Error if the date is invalid or cannot be parsed.
 */
export const extractDate = (
  date: string | null,
  format: string,
): ExtractDateType => {
  if (date === null || date === undefined || date === '') {
    throw new Error('Invalid date')
  }
  const regx = new RegExp('[/\\-\\s]+')
  const dateParts = date.split(regx)
  const formatParts = format.split(regx)

  const retDate = {} as ExtractDateType

  for (let i = 0; i < dateParts.length; i++) {
    if (isNaN(Number(dateParts[i]))) {
      throw new Error('Invalid date')
    } else if (formatParts[i].toLocaleLowerCase().includes('y')) {
      retDate.year = Number(dateParts[i])
    } else if (formatParts[i].toLocaleLowerCase().includes('m')) {
      retDate.month = Number(dateParts[i])
    } else {
      retDate.day = Number(dateParts[i])
    }
  }

  return retDate
}

const CompareYear = (
  base: ExtractDateType,
  compare: ExtractDateType,
  inclusivity: boolean,
): boolean =>
  inclusivity ? base.year <= compare.year : base.year < compare.year

const CompareMonth = (
  base: ExtractDateType,
  compare: ExtractDateType,
  inclusivity: boolean,
): boolean =>
  base.year < compare.year ||
  (base.year === compare.year &&
    (inclusivity ? base.month <= compare.month : base.month < compare.month))

const CompareDay = (
  base: ExtractDateType,
  compare: ExtractDateType,
  inclusivity: boolean,
): boolean =>
  base.year < compare.year ||
  (base.year === compare.year && base.month < compare.month) ||
  (base.year === compare.year &&
    base.month === compare.month &&
    (inclusivity ? base.day <= compare.day : base.day < compare.day))

const compareDateParts = (
  baseParts: ExtractDateType,
  compareParts: ExtractDateType,
  config: DateComparisonValidationConfigType,
): boolean => {
  const yearComparison = CompareYear(
    baseParts,
    compareParts,
    config.inclusivity ?? false,
  )

  if (
    config.granularity === 'year' ||
    config.granularity === 'years' ||
    config.granularity === 'y'
  ) {
    return yearComparison
  }

  const monthComparison = CompareMonth(
    baseParts,
    compareParts,
    config.inclusivity ?? false,
  )

  if (
    config.granularity === 'month' ||
    config.granularity === 'months' ||
    config.granularity === 'm'
  ) {
    return yearComparison && monthComparison
  }

  const dayComparison = CompareDay(
    baseParts,
    compareParts,
    config.inclusivity ?? false,
  )

  return yearComparison && monthComparison && dayComparison
}

/**
 * Determines if baseDate is before dateToCompare based on the specified format, inclusivity, and granularity.
 *
 * @param baseDate - The base date string.
 * @param dateToCompare - The date string to compare against the base date.
 * @param format - The format of the date strings (default is "DD/MM/YYYY").
 * @param config - Configuration object for date comparison.
 * @returns true if baseDate is before dateToCompare, otherwise false.
 * @throws Error if an error occurs during the comparison.
 */
export const DateIsBefore = (
  baseDate: string | null,
  dateToCompare: string | null,
  format: string = payloadDateFormat,
  config: DateComparisonValidationConfigType = {
    inclusivity: false,
    granularity: 'day',
  },
): boolean => {
  if (!baseDate || !dateToCompare) return false

  const baseDateParts = extractDate(baseDate, format)
  const dateToCompareParts = extractDate(dateToCompare, format)
  try {
    if (config.granularity && isDateBaseEnumValue(config.granularity)) {
      return compareDateParts(baseDateParts, dateToCompareParts, config)
    } else if (config.inclusivity) {
      return (
        baseDateParts.year < dateToCompareParts.year ||
        (baseDateParts.year === dateToCompareParts.year &&
          baseDateParts.month < dateToCompareParts.month) ||
        (baseDateParts.year === dateToCompareParts.year &&
          baseDateParts.month === dateToCompareParts.month &&
          baseDateParts.day <= dateToCompareParts.day)
      )
    } else {
      return (
        baseDateParts.year < dateToCompareParts.year ||
        (baseDateParts.year === dateToCompareParts.year &&
          baseDateParts.month < dateToCompareParts.month) ||
        (baseDateParts.year === dateToCompareParts.year &&
          baseDateParts.month === dateToCompareParts.month &&
          baseDateParts.day < dateToCompareParts.day)
      )
    }
  } catch (error) {
    console.error('An error occurred while comparing dates:', error)
    return false
  }
}

/**
 * Determines if baseDate is after dateToCompare based on the specified format, inclusivity, and granularity.
 *
 * @param baseDate - The base date string.
 * @param dateToCompare - The date string to compare against the base date.
 * @param format - The format of the date strings (default is "DD/MM/YYYY").
 * @param config - Configuration object for date comparison.
 * @returns true if baseDate is after dateToCompare, otherwise false.
 * @throws Error if an error occurs during the comparison.
 */
export const DateIsAfter = (
  baseDate: string | null,
  dateToCompare: string | null,
  format: string = payloadDateFormat,
  config: DateComparisonValidationConfigType = {
    inclusivity: false,
    granularity: 'day',
  },
): boolean => {
  if (!baseDate || !dateToCompare) return false

  const baseDateParts = extractDate(baseDate, format)
  const dateToCompareParts = extractDate(dateToCompare, format)
  try {
    if (config.granularity && isDateBaseEnumValue(config.granularity)) {
      return compareDateParts(baseDateParts, dateToCompareParts, config)
    } else if (config.inclusivity) {
      return (
        baseDateParts.year > dateToCompareParts.year ||
        (baseDateParts.year === dateToCompareParts.year &&
          baseDateParts.month > dateToCompareParts.month) ||
        (baseDateParts.year === dateToCompareParts.year &&
          baseDateParts.month === dateToCompareParts.month &&
          baseDateParts.day >= dateToCompareParts.day)
      )
    } else {
      return (
        baseDateParts.year > dateToCompareParts.year ||
        (baseDateParts.year === dateToCompareParts.year &&
          baseDateParts.month > dateToCompareParts.month) ||
        (baseDateParts.year === dateToCompareParts.year &&
          baseDateParts.month === dateToCompareParts.month &&
          baseDateParts.day > dateToCompareParts.day)
      )
    }
  } catch (error) {
    console.error('An error occurred while comparing dates:', error)
    return false
  }
}

/**
 * Validates if a selected date and time falls within the interviewer's availability window.
 * This function is specifically for vendor interview scheduling to ensure the selected
 * date/time is between the interviewer's startTime and endTime from the API response.
 *
 * @param {string} scheduleDate - The selected schedule date in format "DD/MM/YYYY" (e.g., "05/02/2026")
 * @param {string} scheduleTime - The selected schedule time in format "HH:MM AM/PM" (e.g., "11:21 AM")
 * @param {string} startTime - The interviewer's availability start time from API in format "DD-MMM-YYYY HH:mm:ss:IST" (e.g., "23-Jan-2026 15:00:00:IST")
 * @param {string} endTime - The interviewer's availability end time from API in format "DD-MMM-YYYY HH:mm:ss:IST" (e.g., "24-Jan-2026 18:00:00:IST")
 * @returns {boolean} Returns true if the selected date/time is within the availability window, false otherwise
 *
 * @example
 * // Returns true if "05/02/2026 11:21 AM" falls between "04-Feb-2026 09:00:00:IST" and "05-Feb-2026 18:00:00:IST"
 * isWithinInterviewerAvailability("05/02/2026", "11:21 AM", "04-Feb-2026 09:00:00:IST", "05-Feb-2026 18:00:00:IST")
 */
export const isWithinInterviewerAvailability = (
  scheduleDate: string,
  scheduleTime: string,
  startTime: string,
  endTime: string,
): boolean => {
  try {
    if (!scheduleDate || !scheduleTime || !startTime || !endTime) {
      return false
    }

    // Parse the schedule date and time (e.g., "05/02/2026" and "11:21 AM")
    // Combine them into a moment object
    const scheduleDateTimeMoment = moment(
      `${scheduleDate} ${scheduleTime}`,
      // eslint-disable-next-line sonarjs/no-duplicate-string
      'DD/MM/YYYY hh:mm A',
    )

    // Parse the API response format: "23-Jan-2026 15:00:00:IST"
    // Split to remove timezone suffix and parse the date-time
    const parseApiDateTime = (dateTimeStr: string): moment.Moment => {
      // Remove the timezone part (e.g., ":IST")
      const cleanedStr = dateTimeStr.split(':IST')[0]
      // Parse the format "DD-MMM-YYYY HH:mm:ss"
      return moment(cleanedStr, 'DD-MMM-YYYY HH:mm:ss')
    }

    const startMoment = parseApiDateTime(startTime)
    const endMoment = parseApiDateTime(endTime)

    // Validate all parsed dates are valid
    if (
      !scheduleDateTimeMoment.isValid() ||
      !startMoment.isValid() ||
      !endMoment.isValid()
    ) {
      console.warn('Invalid date format detected in availability check')
      return false
    }

    // Check if the selected date-time is between start and end (inclusive)
    return scheduleDateTimeMoment.isBetween(startMoment, endMoment, null, '[]')
  } catch (error) {
    console.error(
      'An error occurred while validating interviewer availability:',
      error,
    )
    return false
  }
}

/**
 * Parses an existing interview into start and end moment objects.
 * Supports two formats:
 * 1. IST format: startTime/endTime in "DD-MMM-YYYY HH:mm:ss:IST"
 * 2. Standard format: interviewDate (DD/MM/YYYY) + interviewTime (HH:MM AM/PM)
 *
 * @param interview - Interview object with datetime information
 * @param durationMinutes - Duration in minutes for standard format interviews
 * @returns Object with start and end moments, or null if invalid
 */
const parseExistingInterviewDateTime = (
  interview: {
    interviewDate?: string
    interviewTime?: string
    startTime?: string
    endTime?: string
  },
  durationMinutes: number,
): { start: moment.Moment; end: moment.Moment } | null => {
  // Format 1: IST format with startTime/endTime
  if (interview.startTime && interview.endTime) {
    const parseApiDateTime = (dateTimeStr: string): moment.Moment => {
      const cleanedStr = dateTimeStr.split(':IST')[0]
      return moment(cleanedStr, 'DD-MMM-YYYY HH:mm:ss')
    }

    const start = parseApiDateTime(interview.startTime)
    const end = parseApiDateTime(interview.endTime)

    if (!start.isValid() || !end.isValid()) {
      console.warn(
        `Invalid IST format: ${interview.startTime} - ${interview.endTime}`,
      )
      return null
    }

    return { start, end }
  }

  // Format 2: Standard format with separate date and time
  if (interview.interviewDate && interview.interviewTime) {
    const start = moment(
      `${interview.interviewDate} ${interview.interviewTime}`,
      'DD/MM/YYYY hh:mm A',
    )

    if (!start.isValid()) {
      console.warn(
        `Invalid date/time format: ${interview.interviewDate} ${interview.interviewTime}`,
      )
      return null
    }

    const end = start.clone().add(durationMinutes, 'minutes')
    return { start, end }
  }

  console.warn('Interview missing required date/time fields:', interview)
  return null
}

/**
 * Checks if a new interview schedule conflicts with existing scheduled interviews.
 * Supports two data formats:
 * 1. Standard format with separate date/time fields: interviewDate (DD/MM/YYYY) + interviewTime (HH:MM AM/PM)
 * 2. IST format with combined datetime: startTime and endTime (DD-MMM-YYYY HH:mm:ss:IST)
 *
 * @param newScheduleDate - The date of the new interview in "DD/MM/YYYY" format
 * @param newScheduleTime - The time of the new interview in "hh:mm AM/PM" format
 * @param existingInterviews - Array of existing scheduled interviews (supports both formats)
 * @param currentInterviewCycleId - Optional interview cycle ID to exclude when rescheduling
 * @param interviewDurationMinutes - Duration of interview in minutes (default: 60)
 * @returns true if there is a conflict, false otherwise
 */
export const hasInterviewTimeConflict = (
  newScheduleDate: string,
  newScheduleTime: string,
  existingInterviews: Array<{
    interviewDate?: string
    interviewTime?: string
    startTime?: string
    endTime?: string
    interviewCycleId?: number
  }>,
  currentInterviewCycleId?: number,
  interviewDurationMinutes = 60,
  // eslint-disable-next-line max-params
): boolean => {
  try {
    if (!newScheduleDate || !newScheduleTime || !existingInterviews) {
      return false
    }

    // Parse the new interview date and time
    const newInterviewStart = moment(
      `${newScheduleDate} ${newScheduleTime}`,
      'DD/MM/YYYY hh:mm A',
    )

    if (!newInterviewStart.isValid()) {
      console.warn('Invalid new interview date/time format')
      return false
    }

    // Calculate end time for the new interview
    const newInterviewEnd = newInterviewStart
      .clone()
      .add(interviewDurationMinutes, 'minutes')

    // Check each existing interview for conflicts
    for (const existingInterview of existingInterviews) {
      // Skip the current interview when rescheduling
      if (
        currentInterviewCycleId &&
        existingInterview.interviewCycleId === currentInterviewCycleId
      ) {
        continue
      }

      // Parse the existing interview using helper function
      const parsedTimes = parseExistingInterviewDateTime(
        existingInterview,
        interviewDurationMinutes,
      )

      if (!parsedTimes) {
        continue // Skip invalid interviews
      }

      const { start: existingStart, end: existingEnd } = parsedTimes

      // Check if the time ranges overlap
      const hasOverlap =
        newInterviewStart.isBefore(existingEnd) &&
        newInterviewEnd.isAfter(existingStart)

      if (hasOverlap) {
        console.log('Interview time conflict detected:', {
          newInterview: {
            start: newInterviewStart.format('DD/MM/YYYY hh:mm A'),
            end: newInterviewEnd.format('DD/MM/YYYY hh:mm A'),
          },
          existingInterview: {
            start: existingStart.format('DD/MM/YYYY hh:mm A'),
            end: existingEnd.format('DD/MM/YYYY hh:mm A'),
          },
        })
        return true
      }
    }

    return false
  } catch (error) {
    console.error(
      'An error occurred while checking interview time conflict:',
      error,
    )
    return false
  }
}

/**
 * Extracts the date part from an IST datetime string in "DD-MMM-YYYY HH:mm:ss:IST" format
 * and returns it in "DD MMM YYYY" format for comparison.
 *
 * @param istDateTimeStr - DateTime string in format "20-Jan-2026 14:00:00:IST"
 * @returns Date string in format "20 Jan 2026" or empty string if invalid
 *
 * @example
 * extractDateFromISTDateTime("20-Jan-2026 14:00:00:IST") // Returns "20 Jan 2026"
 */
export const extractDateFromISTDateTime = (istDateTimeStr: string): string => {
  try {
    if (!istDateTimeStr) return ''

    // Remove the :IST timezone suffix
    const cleanedStr = istDateTimeStr.split(':IST')[0].trim()

    // Parse the format "DD-MMM-YYYY HH:mm:ss"
    const parsedMoment = moment(cleanedStr, 'DD-MMM-YYYY HH:mm:ss', true)

    if (!parsedMoment.isValid()) {
      console.warn(
        `extractDateFromISTDateTime: Invalid date format - ${istDateTimeStr}`,
      )
      return ''
    }

    // Return in format "DD MMM YYYY" (e.g., "20 Jan 2026")
    return parsedMoment.format('DD MMM YYYY')
  } catch (error) {
    console.error(
      'An error occurred while extracting date from IST datetime:',
      error,
    )
    return ''
  }
}

/**
 * Validates that the dates extracted from startTime and endTime (in IST format)
 * match the provided fromDate and toDate.
 *
 * This ensures consistency when scheduling/rescheduling interviews - the date portion
 * of the time fields must match the separate date fields.
 *
 * @param startTime - Start datetime in format "DD-MMM-YYYY HH:mm:ss:IST" (e.g., "20-Jan-2026 14:00:00:IST")
 * @param endTime - End datetime in format "DD-MMM-YYYY HH:mm:ss:IST" (e.g., "21-Jan-2026 15:00:00:IST")
 * @param fromDate - Expected from date in format "DD MMM YYYY" (e.g., "20 Jan 2026")
 * @param toDate - Expected to date in format "DD MMM YYYY" (e.g., "21 Jan 2026")
 * @returns Object with isValid boolean and error message if invalid
 *
 * @example
 * validateDateTimeConsistency(
 *   "20-Jan-2026 14:00:00:IST",
 *   "21-Jan-2026 15:00:00:IST",
 *   "20 Jan 2026",
 *   "21 Jan 2026"
 * ) // Returns { isValid: true, errorMessage: '' }
 */
export const validateDateTimeConsistency = (
  startTime: string,
  endTime: string,
  fromDate: string,
  toDate: string,
): { isValid: boolean; errorMessage: string } => {
  try {
    if (!startTime || !endTime || !fromDate || !toDate) {
      return {
        isValid: false,
        errorMessage: 'All date/time fields are required for validation',
      }
    }

    // Extract dates from IST datetime strings
    const extractedStartDate = extractDateFromISTDateTime(startTime)
    const extractedEndDate = extractDateFromISTDateTime(endTime)

    if (!extractedStartDate || !extractedEndDate) {
      return {
        isValid: false,
        errorMessage: 'Invalid start time or end time format',
      }
    }

    // Normalize both extracted and provided dates for comparison (trim and case-insensitive)
    const normalizeDate = (date: string): string =>
      date.trim().toLowerCase().replace(/\s+/g, ' ')

    const normalizedExtractedStart = normalizeDate(extractedStartDate)
    const normalizedExtractedEnd = normalizeDate(extractedEndDate)
    const normalizedFromDate = normalizeDate(fromDate)
    const normalizedToDate = normalizeDate(toDate)

    // Check if dates match
    const startDateMatches = normalizedExtractedStart === normalizedFromDate
    const endDateMatches = normalizedExtractedEnd === normalizedToDate

    if (!startDateMatches || !endDateMatches) {
      let errorMessage = 'Date mismatch detected: '
      if (!startDateMatches) {
        errorMessage += `Start date from time (${extractedStartDate}) does not match From Date (${fromDate}). `
      }
      if (!endDateMatches) {
        errorMessage += `End date from time (${extractedEndDate}) does not match To Date (${toDate}).`
      }
      return { isValid: false, errorMessage }
    }

    return { isValid: true, errorMessage: '' }
  } catch (error) {
    console.error(
      'An error occurred while validating date/time consistency:',
      error,
    )
    return {
      isValid: false,
      errorMessage: 'An unexpected error occurred during validation',
    }
  }
}

/**
 * Converts a date string from "DD/MM/YYYY" format to "DD MMM YYYY" format.
 * Used to convert schedule dates to match the format expected by validation.
 *
 * @param dateStr - Date string in format "DD/MM/YYYY" (e.g., "20/01/2026")
 * @returns Date string in format "DD MMM YYYY" (e.g., "20 Jan 2026") or empty string if invalid
 *
 * @example
 * convertDateToDisplayFormat("20/01/2026") // Returns "20 Jan 2026"
 */
export const convertDateToDisplayFormat = (dateStr: string): string => {
  try {
    if (!dateStr) return ''

    const parsedMoment = moment(dateStr, 'DD/MM/YYYY', true)

    if (!parsedMoment.isValid()) {
      console.warn(`convertDateToDisplayFormat: Invalid date - ${dateStr}`)
      return ''
    }

    return parsedMoment.format('DD MMM YYYY')
  } catch (error) {
    console.error('An error occurred while converting date format:', error)
    return ''
  }
}

//! DO NOT ADD ANY CODE BELOW HERE
//! DO NOT ADD ANY CODE BELOW HERE
//! DO NOT ADD ANY CODE BELOW HERE
//! SCROLL UP TO ADD YOUR CODES
